import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../services/unifiedApiClient';
import { withErrorHandling } from '../services/errorHandler';
import { LoadingTypes } from '../services/loadingService';
import { LoadingSpinner, ErrorMessage } from '../design-system';

const OAuthCallbackSecure = () => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const { handleOAuthCallback } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const exchangeCodeForTokens = async () => {
      try {
        setError(null);

        // Parse query parameters to get the auth code and state
        const params = new URLSearchParams(location.search);
        const code = params.get('code');
        const urlState = params.get('state');

        if (!code || code.trim() === '') {
          throw new Error('No authorization code found in URL');
        }

        // Verify OAuth state parameter to prevent CSRF
        const storedState = sessionStorage.getItem('oauth_state');
        if (storedState) {
          if (!urlState || urlState !== storedState) {
            sessionStorage.removeItem('oauth_state');
            throw new Error('OAuth state mismatch — possible CSRF attack. Please try again.');
          }
          sessionStorage.removeItem('oauth_state');
        }

        // Exchange the auth code for tokens with enhanced error handling
        const { data: tokenData, error: tokenError } = await withErrorHandling(
          () => apiClient.postWithLoading('/exchange-auth-code', null, LoadingTypes.AUTH_OAUTH, {
            params: { code },
            withCredentials: true
          }),
          {
            customMessages: {
              AUTHENTICATION: 'OAuth authentication failed. Please try again.',
              VALIDATION_ERROR: 'Your login session has expired. Please try logging in again.',
              SERVER_ERROR: 'Server error during OAuth flow. Please try again later.'
            }
          }
        );

        if (tokenError) {
          if (tokenError.status === 400) {
            setError(tokenError.message);
            setLoading(false);
            return;
          }
          throw new Error(tokenError.message);
        }

        if (!tokenData || !tokenData.access_token) {
          throw new Error('No access token returned from server');
        }

        // Store the tokens
        localStorage.setItem('token', tokenData.access_token);

        // Verify the token was stored correctly
        const storedToken = localStorage.getItem('token');
        if (!storedToken) {
          throw new Error('Failed to store authentication token');
        }

        if (tokenData.refresh_token) {
          localStorage.setItem('refresh_token', tokenData.refresh_token);
        }

        // Fetch user data
        const { data: userData, error: userError } = await withErrorHandling(
          () => apiClient.get('/auth/me'),
          {
            customMessages: {
              AUTHENTICATION: 'Failed to fetch user data. Please try logging in again.',
              SERVER_ERROR: 'Server error while fetching user data. Please try again.'
            }
          }
        );

        if (userError) {
          throw new Error(userError.message);
        }

        // Ensure tokens are stored in localStorage
        localStorage.setItem('token', tokenData.access_token);
        if (tokenData.refresh_token) {
          localStorage.setItem('refresh_token', tokenData.refresh_token);
        }

        // Update auth context with user data
        await handleOAuthCallback(userData);

        // Double-check that token is in localStorage
        const token = localStorage.getItem('token');
        if (!token) {
          console.error('Token not found in localStorage after handleOAuthCallback');
          // Set token again to be sure
          localStorage.setItem('token', tokenData.access_token);
        }

        // Set a flag in sessionStorage to indicate successful login
        sessionStorage.setItem('oauth_login_success', 'true');

        // Navigate to dashboard using React Router
        navigate('/dashboard');
      } catch (err) {
        console.error('Error in authentication flow:', err);

        // Handle expired or invalid authorization code
        if (err.response && err.response.status === 400) {
          setError('Authorization code expired or invalid. Please try logging in again.');
        } else {
          setError(err.message || 'Authentication failed');
        }
      } finally {
        setLoading(false);
      }
    };

    exchangeCodeForTokens();
  }, [location, navigate, handleOAuthCallback]);

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-dark-bg flex items-center justify-center">
        <div className="flex flex-col items-center">
          <LoadingSpinner size="large" />
          <p className="text-brand-text mt-4">Completing authentication...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-brand-dark-bg flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <ErrorMessage message={error} className="mb-6" />
          <div className="text-center">
            <p className="text-brand-muted-text mb-4">
              Authentication failed. Please try again or use another authentication method.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="btn btn-primary px-6 py-2 rounded-lg font-medium"
            >
              Return to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-dark-bg flex items-center justify-center">
      <div className="flex flex-col items-center">
        <LoadingSpinner size="large" />
        <p className="text-brand-text mt-4">Authentication successful! Redirecting to dashboard...</p>
      </div>
    </div>
  );
};

export default OAuthCallbackSecure;
